import { createSlotMarker, findSlotMarkers } from "../model/slots"

export type SourceRun =
  | { kind: "text"; text: string }
  | { kind: "slot"; slotId: string; label: string }

/** Labels are presentation only; neither labels nor their equality identify slots. */
export type SlotLabelResolver = (
  slotId: string,
  sourceOffset: number,
  ordinal: number,
) => string

export interface TokenMapping {
  kind: SourceRun["kind"]
  sourceStart: number
  sourceLength: number
  displayStart: number
  displayLength: number
}

/** Quill-style, half-open UTF-16 range. Embeds occupy exactly one display unit. */
export interface EditorRange {
  index: number
  length: number
}

export function toSourceRuns(
  source: string,
  label?: SlotLabelResolver,
): SourceRun[] {
  const runs: SourceRun[] = []
  const ordinals = new Map<string, number>()
  let start = 0
  for (const marker of findSlotMarkers(source)) {
    if (marker.offset > start)
      runs.push({ kind: "text", text: source.slice(start, marker.offset) })
    if (!ordinals.has(marker.slotId))
      ordinals.set(marker.slotId, ordinals.size + 1)
    const ordinal = ordinals.get(marker.slotId)!
    runs.push({
      kind: "slot",
      slotId: marker.slotId,
      label:
        label?.(marker.slotId, marker.offset, ordinal) ?? `Ссылка ${ordinal}`,
    })
    start = marker.offset + marker.length
  }
  if (start < source.length)
    runs.push({ kind: "text", text: source.slice(start) })
  return runs
}

export function fromSourceRuns(runs: readonly SourceRun[]): string {
  return runs
    .map((run) =>
      run.kind === "text" ? run.text : createSlotMarker(run.slotId),
    )
    .join("")
}

export function buildSourceMapping(runs: readonly SourceRun[]): TokenMapping[] {
  let sourceStart = 0
  let displayStart = 0
  const mapping: TokenMapping[] = []
  const append = (
    kind: SourceRun["kind"],
    sourceLength: number,
    displayLength: number,
  ) => {
    if (!sourceLength) return
    mapping.push({
      kind,
      sourceStart,
      sourceLength,
      displayStart,
      displayLength,
    })
    sourceStart += sourceLength
    displayStart += displayLength
  }
  for (const run of runs) {
    if (run.kind === "slot")
      append("slot", createSlotMarker(run.slotId).length, 1)
    else {
      // Quill folds CRLF into one newline. Keep original bytes and map the
      // indivisible pair instead of letting Quill silently shift all indices.
      for (const part of run.text.split(/(\r\n)/))
        append("text", part.length, part === "\r\n" ? 1 : part.length)
    }
  }
  return mapping
}

function mapRange(
  range: EditorRange,
  mapping: readonly TokenMapping[],
  toSource: boolean,
): EditorRange {
  const startKey = toSource ? "displayStart" : "sourceStart"
  const lengthKey = toSource ? "displayLength" : "sourceLength"
  const targetStart = toSource ? "sourceStart" : "displayStart"
  const targetLength = toSource ? "sourceLength" : "displayLength"
  const last = mapping[mapping.length - 1]
  const total = last ? last[startKey] + last[lengthKey] : 0
  if (
    !Number.isSafeInteger(range.index) ||
    !Number.isSafeInteger(range.length) ||
    range.index < 0 ||
    range.length < 0 ||
    range.index + range.length > total
  ) {
    throw new RangeError("Selection is outside the UTF-16 document")
  }
  const endpoint = (position: number, end: boolean) => {
    for (const entry of mapping) {
      if (position < entry[startKey] + entry[lengthKey]) {
        const local = position - entry[startKey]
        if (
          (entry.kind === "slot" ||
            entry.sourceLength !== entry.displayLength) &&
          local > 0
        )
          return entry[targetStart] + (end ? entry[targetLength] : 0)
        return entry[targetStart] + local
      }
    }
    return last ? last[targetStart] + last[targetLength] : 0
  }
  const index = endpoint(range.index, false)
  // A caret inside a serialized token snaps before it. Nonempty selections
  // expand outwards, so no operation can address a fragment of an embed.
  const end = range.length ? endpoint(range.index + range.length, true) : index
  return { index, length: end - index }
}

export function displayRangeToSource(
  range: EditorRange,
  mapping: readonly TokenMapping[],
): EditorRange {
  return mapRange(range, mapping, true)
}

export function sourceRangeToDisplay(
  range: EditorRange,
  mapping: readonly TokenMapping[],
): EditorRange {
  return mapRange(range, mapping, false)
}
